import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { CustomerAddressDto, CustomerDetailsDto } from 'types/api/customer.type';
import {
  AddOrderItemPayload,
  BudgetStats,
  DeliveryCodeDto,
  Order,
  OrderCharts,
  OrderCreationResponse,
  OrderHeaderResponse,
  OrderIntakeWeek,
  OrderItem,
  OrderItemMutationResponse,
  OrderItemsMutationResponse,
  OrderStatsAgent,
  OrderStatsBreakdown,
  OrderStatsBundle,
  OrderStatsScope,
  OrderTopStyle,
  OrderUpdatePayload,
  Takeover,
  TopStylesScope,
  ZoneStatsGroupBy,
  ZoneStatsRow,
} from 'types/api/orders.type';

import { tokenStore } from 'lib/tokenStore';

// ---------------------------------------------------------------------------
// Base query with auth header
// ---------------------------------------------------------------------------

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_URL}/api`,
  prepareHeaders: (headers) => {
    const token = tokenStore.getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// ---------------------------------------------------------------------------
// Tag types for cache invalidation
// ---------------------------------------------------------------------------

type OrdersTagTypes =
  | 'OrderHeader'
  | 'OrdersList'
  | 'DropdownOrders'
  | 'CustomerDetails'
  | 'CustomerAddresses'
  | 'DeliveryCodes'
  | 'Scales'
  | 'Takeovers';

// ---------------------------------------------------------------------------
// API definition
// ---------------------------------------------------------------------------

type OrdersListParams = {
  page?: number;
  size?: number;
  customerCode?: string;
  zone?: string;
  season?: string;
  marketingLine?: string;
  userAlias?: string;
};

type OrdersListResponse = {
  content: Order[];
  totalPages: number;
  totalElements: number;
};

type CustomerSearchResult = {
  id: number;
  customerCode: string;
  displayName: string;
  commercialZoneCode: string;
  status: string;
  customerTypeCode: string;
};

export const ordersApi = createApi({
  reducerPath: 'ordersApi',
  baseQuery,
  tagTypes: [
    'OrderHeader',
    'OrdersList',
    'DropdownOrders',
    'CustomerDetails',
    'CustomerAddresses',
    'DeliveryCodes',
    'Scales',
    'Takeovers',
  ] as OrdersTagTypes[],
  endpoints: (builder) => ({
    // ─── Order Header (detail page) ──────────────────────────────────────────
    getOrderById: builder.query<OrderHeaderResponse, string>({
      query: (orderId) => `/v1/orders/${orderId}`,
      providesTags: (_result, _error, orderId) => [{ type: 'OrderHeader', id: orderId }],
    }),

    // ─── Order charts ("Show charts" section of the detail page) ─────────────
    // Same tag as the header, so item add/delete and header saves refresh it
    // while the section is open. Quantity saves invalidate nothing (see
    // updateItemSizes), so the caller passes a `revision` of the order totals:
    // it is not sent, it only makes each totals change its own cache entry and
    // therefore a fresh request.
    getOrderCharts: builder.query<OrderCharts, { orderId: string; revision: string }>({
      query: ({ orderId }) => `/v1/orders/${orderId}/charts`,
      providesTags: (_result, _error, { orderId }) => [{ type: 'OrderHeader', id: orderId }],
    }),

    // ─── Orders List (paginated) ─────────────────────────────────────────────
    getOrders: builder.query<OrdersListResponse, OrdersListParams>({
      query: ({
        page = 0,
        size = 20,
        customerCode,
        zone,
        season,
        marketingLine,
        userAlias,
      }) => ({
        url: '/v1/orders',
        params: {
          page,
          size,
          ...(customerCode ? { customerCode } : {}),
          ...(zone ? { zone } : {}),
          ...(season ? { season } : {}),
          ...(marketingLine ? { marketingLine } : {}),
          ...(userAlias ? { userAlias } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Order stats for the list page (same scope/filters as getOrders) ─────
    // Returns two scopes: `mine` (My sales) and `zone` (the filtered list).
    // Passes the same filters as getOrders so the KPI numbers match the list.
    getOrderStats: builder.query<OrderStatsBundle, Omit<OrdersListParams, 'page' | 'size'>>({
      query: ({ customerCode, zone, season, marketingLine, userAlias }) => ({
        url: '/v1/orders/stats',
        params: {
          ...(customerCode ? { customerCode } : {}),
          ...(zone ? { zone } : {}),
          ...(season ? { season } : {}),
          ...(marketingLine ? { marketingLine } : {}),
          ...(userAlias ? { userAlias } : {}),
        },
      }),
      // Reuse the OrdersList tag so order create/delete/duplicate auto-refresh stats.
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: top styles by ordered units ────────────────────────
    // scope=zone (default) honours the caller's zone(s); scope=company ranks
    // over the whole company but the BE returns aggregates only.
    getTopStyles: builder.query<
      OrderTopStyle[],
      {
        season: string;
        zone?: string;
        scope?: TopStylesScope;
        userAlias?: string;
        customerCode?: string;
        marketingLine?: string;
        limit?: number;
      }
    >({
      query: ({ season, zone, scope, userAlias, customerCode, marketingLine, limit }) => ({
        url: '/v1/orders/stats/top-styles',
        params: {
          season,
          ...(zone ? { zone } : {}),
          ...(scope ? { scope } : {}),
          ...(userAlias ? { userAlias } : {}),
          ...(customerCode ? { customerCode } : {}),
          ...(marketingLine ? { marketingLine } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: order intake per ISO week ──────────────────────────
    // scope mine|zone, same semantics as getOrderStats.
    getOrderIntake: builder.query<
      OrderIntakeWeek[],
      {
        season: string;
        zone?: string;
        scope?: OrderStatsScope;
        userAlias?: string;
        customerCode?: string;
        marketingLine?: string;
      }
    >({
      query: ({ season, zone, scope, userAlias, customerCode, marketingLine }) => ({
        url: '/v1/orders/stats/intake',
        params: {
          season,
          ...(zone ? { zone } : {}),
          ...(scope ? { scope } : {}),
          ...(userAlias ? { userAlias } : {}),
          ...(customerCode ? { customerCode } : {}),
          ...(marketingLine ? { marketingLine } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: units by gender / category / fabric ────────────────
    // scope zone|company, same semantics as getTopStyles; all three sections
    // are rolled up from one BE aggregation, so their totals always agree.
    getStatsBreakdown: builder.query<
      OrderStatsBreakdown,
      {
        season: string;
        zone?: string;
        scope?: TopStylesScope;
        userAlias?: string;
        customerCode?: string;
        marketingLine?: string;
        /** Catalogue category (Node_Name2) — narrows the colour sections only. */
        category?: string;
      }
    >({
      query: ({ season, zone, scope, userAlias, customerCode, marketingLine, category }) => ({
        url: '/v1/orders/stats/breakdown',
        params: {
          season,
          ...(zone ? { zone } : {}),
          ...(scope ? { scope } : {}),
          ...(userAlias ? { userAlias } : {}),
          ...(customerCode ? { customerCode } : {}),
          ...(marketingLine ? { marketingLine } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: sales aggregated by zone / business area ───────────
    // groupBy=zone (default) → one row per zone; groupBy=area → server-side
    // business-area rollups. Same shared filters as the other stats widgets.
    getStatsByZone: builder.query<
      ZoneStatsRow[],
      {
        season: string;
        zone?: string;
        userAlias?: string;
        customerCode?: string;
        marketingLine?: string;
        groupBy?: ZoneStatsGroupBy;
      }
    >({
      query: ({ season, zone, userAlias, customerCode, marketingLine, groupBy }) => ({
        url: '/v1/orders/stats/by-zone',
        params: {
          season,
          ...(zone ? { zone } : {}),
          ...(userAlias ? { userAlias } : {}),
          ...(customerCode ? { customerCode } : {}),
          ...(marketingLine ? { marketingLine } : {}),
          ...(groupBy ? { groupBy } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: budget vs actual for the season ────────────────────
    getBudgetStats: builder.query<
      BudgetStats,
      { season: string; zone?: string; marketingLine?: string }
    >({
      query: ({ season, zone, marketingLine }) => ({
        url: '/v1/orders/stats/budget',
        params: {
          season,
          ...(zone ? { zone } : {}),
          ...(marketingLine ? { marketingLine } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Statistics page: salespeople for the agent filter ───────────────────
    getStatsAgents: builder.query<
      OrderStatsAgent[],
      { season?: string; zone?: string; marketingLine?: string }
    >({
      query: ({ season, zone, marketingLine }) => ({
        url: '/v1/orders/stats/agents',
        params: {
          ...(season ? { season } : {}),
          ...(zone ? { zone } : {}),
          // Keeps the dropdown in step with the page's line filter: without it
          // the list offered agents with no orders in the selected line.
          ...(marketingLine ? { marketingLine } : {}),
        },
      }),
      providesTags: ['OrdersList'],
    }),

    // ─── Dropdown orders (settings bar — paginated, loaded incrementally) ────────
    getDropdownOrders: builder.query<
      { orders: Order[]; hasMore: boolean },
      {
        zone?: string;
        page?: number;
        size?: number;
        season?: string;
        marketingLine?: string;
      }
    >({
      query: ({ zone, page = 0, size = 20, season, marketingLine }) => ({
        url: '/v1/orders',
        params: {
          size,
          page,
          ...(zone ? { zone } : {}),
          ...(season ? { season } : {}),
          ...(marketingLine ? { marketingLine } : {}),
        },
      }),
      transformResponse: (response: OrdersListResponse, _meta, arg) => ({
        orders: response.content ?? [],
        hasMore: (arg.page ?? 0) + 1 < (response.totalPages ?? 0),
      }),
      providesTags: ['DropdownOrders'],
    }),

    // ─── Update order (partial PUT — only changed fields) ────────────────────
    updateOrder: builder.mutation<
      OrderHeaderResponse,
      { orderId: string; data: Partial<OrderUpdatePayload> }
    >({
      query: ({ orderId, data }) => ({
        url: `/v1/orders/${orderId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderHeader', id: orderId },
        'OrdersList',
        'DropdownOrders',
      ],
      // Optimistic update: patch the cached order header immediately
      async onQueryStarted({ orderId, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          ordersApi.util.updateQueryData('getOrderById', orderId, (draft) => {
            Object.assign(draft, data);
          })
        );
        try {
          const { data: saved } = await queryFulfilled;
          // An empty answer (a proxy's 204, an empty 200) is no reason to undo a save that succeeded: the
          // refetch that follows brings the stored values.
          if (!saved) return;
          // The answer carries what the backend STORED. A customer change keeps or clears the addresses
          // (customer-connection-fix §6.3.4), and the optimistic patch above only holds the request body, so
          // without this the screen would show stale addresses whenever the refetch that follows fails. Only
          // these three fields: the answer has no signed thumbnail URLs, which the header may hold.
          dispatch(
            ordersApi.util.updateQueryData('getOrderById', orderId, (draft) => {
              if (saved.customerCode !== undefined) draft.customerCode = saved.customerCode;
              if (saved.shippingAddressCode !== undefined) {
                draft.shippingAddressCode = saved.shippingAddressCode;
              }
              if (saved.goodsDestinationCode !== undefined) {
                draft.goodsDestinationCode = saved.goodsDestinationCode;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    // ─── Update order notes (no refetch — avoids flickering) ────────────────
    updateOrderNotes: builder.mutation<
      OrderHeaderResponse,
      { orderId: string; data: Pick<OrderUpdatePayload, 'note' | 'internalNote'> }
    >({
      query: ({ orderId, data }) => ({
        url: `/v1/orders/${orderId}`,
        method: 'PUT',
        body: data,
      }),
      // No invalidatesTags — optimistic update is sufficient for text fields.
      async onQueryStarted({ orderId, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          ordersApi.util.updateQueryData('getOrderById', orderId, (draft) => {
            Object.assign(draft, data);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // ─── Create order ────────────────────────────────────────────────────────
    createOrder: builder.mutation<
      OrderCreationResponse,
      {
        season: string;
        zone: string;
        warehouseInternalCode: string;
        customerCode: string;
        collectionCode?: string;
      }
    >({
      query: (body) => ({
        url: '/v1/orders',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OrdersList', 'DropdownOrders'],
    }),

    // ─── Delete order ────────────────────────────────────────────────────────
    deleteOrder: builder.mutation<void, string>({
      query: (orderId) => ({
        url: `/v1/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['OrdersList', 'DropdownOrders'],
    }),

    // ─── Duplicate order ─────────────────────────────────────────────────────
    duplicateOrder: builder.mutation<
      OrderCreationResponse,
      {
        orderId: string;
        /** Left out by Copy Order: the copy always takes the source order's own season. */
        season?: string;
        zone: string;
        customerCode: string;
        /** '' stores none; left out, the BE keeps the source order's (Copy Order always sends both). */
        shippingAddressCode?: string;
        goodsDestinationCode?: string;
      }
    >({
      query: ({ orderId, ...body }) => ({
        url: `/v1/orders/${orderId}/duplicate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['OrdersList', 'DropdownOrders'],
    }),

    // ─── Item mutations ──────────────────────────────────────────────────────
    updateItemQuantities: builder.mutation<
      OrderItemMutationResponse,
      { orderId: string; itemId: string; size: string; quantity: number }
    >({
      query: ({ orderId, itemId, size, quantity }) => ({
        url: `/v1/orders/${orderId}/items/${encodeURIComponent(itemId)}`,
        method: 'PATCH',
        body: { sizes: [{ size, quantity }] },
      }),
      // Invalidate the order header to refresh totals
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderHeader', id: orderId },
      ],
    }),

    // Order table: every pending size of one item in a single PATCH. No tag
    // invalidation — the response already carries the item and the order
    // totals, and refetching the whole order after every save both slowed the
    // backend down and could put stale quantities back into the grid.
    updateItemSizes: builder.mutation<
      OrderItemMutationResponse,
      { orderId: string; itemId: string; sizes: { size: string; quantity: number }[] }
    >({
      query: ({ orderId, itemId, sizes }) => ({
        url: `/v1/orders/${orderId}/items/${encodeURIComponent(itemId)}`,
        method: 'PATCH',
        body: { sizes },
      }),
    }),

    // Batch of updateItemSizes: every edited item in one request. Each item is
    // saved on its own (failures come back per item in `results`); the order
    // totals are computed once for the whole batch.
    updateItemsSizes: builder.mutation<
      OrderItemsMutationResponse,
      {
        orderId: string;
        items: Array<{ itemId: string; sizes: { size: string; quantity: number }[] }>;
      }
    >({
      query: ({ orderId, items }) => ({
        url: `/v1/orders/${orderId}/items`,
        method: 'PATCH',
        body: { items },
      }),
    }),

    deleteItem: builder.mutation<
      OrderItemMutationResponse,
      { orderId: string; itemId: string }
    >({
      query: ({ orderId, itemId }) => ({
        url: `/v1/orders/${orderId}/items/${encodeURIComponent(itemId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderHeader', id: orderId },
      ],
    }),

    addItem: builder.mutation<OrderItem, { orderId: string; data: AddOrderItemPayload }>({
      query: ({ orderId, data }) => ({
        url: `/v1/orders/${orderId}/items`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'OrderHeader', id: orderId },
      ],
    }),

    // ─── Customer details (by customer code) ─────────────────────────────────
    getCustomerByCode: builder.query<
      { details: CustomerDetailsDto; searchResult: CustomerSearchResult },
      { customerCode: string }
    >({
      async queryFn({ customerCode }, _api, _extraOptions, baseQueryFn) {
        if (!customerCode || customerCode.trim() === '') {
          return { error: { status: 'CUSTOM_ERROR', error: 'No customer code' } as any };
        }

        // Step 1: search for the customer
        const searchRes = await baseQueryFn({
          url: '/v1/customers/search',
          params: { q: customerCode, page: 0, size: 1000 },
        });
        if (searchRes.error) return { error: searchRes.error };

        const searchData = searchRes.data as { content: CustomerSearchResult[] };
        const rows = searchData.content ?? [];
        const exact = rows.find((r) => r.customerCode === customerCode) ?? rows[0];
        if (!exact) {
          return { error: { status: 'CUSTOM_ERROR', error: 'Customer not found' } as any };
        }

        // Step 2: get details
        const detailsRes = await baseQueryFn({
          url: `/v1/customers/${exact.id}/details`,
        });
        if (detailsRes.error) return { error: detailsRes.error };

        return {
          data: {
            details: detailsRes.data as CustomerDetailsDto,
            searchResult: exact,
          },
        };
      },
      providesTags: (_result, _error, { customerCode }) => [
        { type: 'CustomerDetails', id: customerCode },
      ],
    }),

    // ─── Customer addresses ──────────────────────────────────────────────────
    getCustomerAddresses: builder.query<
      CustomerAddressDto[],
      { customerId: number; type: string }
    >({
      query: ({ customerId, type }) => ({
        url: `/v1/customers/${customerId}/addresses`,
        params: { type },
      }),
      providesTags: (_result, _error, { customerId, type }) => [
        { type: 'CustomerAddresses', id: `${customerId}-${type}` },
      ],
    }),

    // ─── Delivery codes ──────────────────────────────────────────────────────
    getDeliveryCodes: builder.query<
      DeliveryCodeDto[],
      { season: string; linesheet?: string; isActive?: boolean }
    >({
      query: (params) => ({
        url: '/v1/delivery-codes',
        params,
      }),
      providesTags: (_result, _error, { season, linesheet }) => [
        { type: 'DeliveryCodes', id: `${season}-${linesheet ?? ''}` },
      ],
    }),

    // ─── Constants ───────────────────────────────────────────────────────────
    getScales: builder.query<{ [key: string]: string[] }, void>({
      query: () => '/v1/orders/constants/scale',
      providesTags: ['Scales'],
    }),

    getTakeovers: builder.query<Takeover[], void>({
      query: () => '/v1/orders/constants/takeovers',
      providesTags: ['Takeovers'],
    }),
  }),
});

export const {
  useGetOrderByIdQuery,
  useGetOrderChartsQuery,
  useGetOrdersQuery,
  useGetOrderStatsQuery,
  useGetTopStylesQuery,
  useGetOrderIntakeQuery,
  useGetStatsAgentsQuery,
  useGetStatsBreakdownQuery,
  useGetStatsByZoneQuery,
  useGetBudgetStatsQuery,
  useGetDropdownOrdersQuery,
  useUpdateOrderMutation,
  useUpdateOrderNotesMutation,
  useCreateOrderMutation,
  useDeleteOrderMutation,
  useDuplicateOrderMutation,
  useUpdateItemQuantitiesMutation,
  useUpdateItemSizesMutation,
  useUpdateItemsSizesMutation,
  useDeleteItemMutation,
  useAddItemMutation,
  useGetCustomerByCodeQuery,
  useGetCustomerAddressesQuery,
  useGetDeliveryCodesQuery,
  useGetScalesQuery,
  useGetTakeoversQuery,
} = ordersApi;
